import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { Subject, forkJoin } from 'rxjs';
import { ClientProfileService } from '@pg-app/client/services/client-profile.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { UserService } from '@pg-app/partner/user-profile/services/user-service';
import { ShareService } from '@nttp/common-service/share-service';
import { environment } from '@pg-env/environment';

export interface CommonModel {
  name: string;
  id: string;
}
export interface Questions {
  id: string;
  content: string;
}
export interface Country {
  id: string;
  code: string;
  name: string;
}

@Component({
  selector: 'np-client-profile',
  templateUrl: './client-profile.component.html',
  styleUrls: ['./client-profile.component.scss']
})
export class ClientProfileComponent implements OnInit {
  titles: CommonModel[];
  countries: Country[];
  clientProfile: FormGroup;

  collapseMyProfile = false;
  collapseAccountInfo = true;
  collapseSubRef = true;

  defaultRMPhone = '+44 20 3880 0572';
  destroy$ = new Subject<boolean>();

  acceptMarketingEmails = false;
  submitForm = false;
  acceptPolicy = true;

  clientData = null;
  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  domain = environment.domain;
  brandName = environment.brandName;

  constructor(
    private fb: FormBuilder,
    private clientProfileService: ClientProfileService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private userService: UserService,
    private shareService: ShareService
  ) {}

  ngOnInit() {
    this.clientProfile = this.fb.group({
      accountNumber: '',
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      birthday: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      address3: '',
      postCode: '',
      countyState: '',
      country: '',
      name: '',
      emailAccount: '',
      phoneAccount: '',
    });

    this.clientProfile.get('phoneAccount').setValue(this.defaultRMPhone);
    const userProfile = this.userService.getAccountInfo();
    const additionalDetail = this.clientProfileService.getAdditionalClientDetail();
    const titleList = this.shareService.titleList$;
    const countryList = this.shareService.countryList$;

    forkJoin([userProfile, additionalDetail, titleList, countryList]).subscribe(res => {
      this.getAllData(res);
    });
  }

  getAllData(data) {
    this.titles = data[2] || [];
    this.countries = data[3] || [];
    this.getUserProfile(data[0]);
    this.getClientAdditionalDetail(data[1]);
  }

  onSubmit() {
    this.collapseMyProfile = false;
    this.collapseAccountInfo = false;
    this.collapseSubRef = false;
    this.submitForm = true;
    this.clientProfile.markAllAsTouched();

    if (this.clientProfile.valid && this.acceptPolicy) {
      this.updateClientProfile();
    }
  }

  private updateClientProfile() {
    const clientDetail = {
      leadType: this.clientData.leadType,
      firstName: this.clientData.firstName,
      middleName: this.clientData.middleName ? this.clientData.middleName : null,
      lastName: this.clientData.lastName,
      email: this.clientData.email,
      phoneNumber: this.clientData.phoneNumber,
      doNotBulkEmail : !this.acceptMarketingEmails
    };

    this.clientProfileService.updateClientDetail(clientDetail).subscribe(
      data => {
        this.dialog.open(CommonDialogComponent, {
          disableClose: true,
          data: {
            title: 'Profile updated successfully!',
            dialogType: 1,
            routingUrl: 'client/dashboard'
          }
        });
      }
    );
  }

  private getUserProfile(userProfile) {
    if (userProfile) {
      const phone = userProfile.phoneNumber.split(' ').join('');
      this.clientProfile.patchValue({
        firstName: userProfile.firstName,
        middleName: userProfile.middleName,
        lastName: userProfile.lastName,
        email: userProfile.email,
        phone: phone
      });
    }
  }

  getClientAdditionalDetail(additionalData) {
    if (additionalData) {
      this.clientProfile.patchValue({
        address1: additionalData.address1,
        address2: additionalData.address2,
        address3: additionalData.address3,
        postCode: additionalData.postCode,
        countyState: additionalData.city,
        country: additionalData.country.id,
        accountNumber: additionalData.accountNumber,
        title: additionalData.title.titleId
      });

      if (additionalData.dateOfBirth) {
        this.clientProfile.get('birthday').setValue(new Date(additionalData.dateOfBirth));
      }

      const privalgo = additionalData.leadManagers;
      if (privalgo.length > 0) {
        this.clientProfile.get('name').setValue(privalgo[0].salePerson.fullName);
        this.clientProfile.get('emailAccount').setValue(privalgo[0].salePerson.email);
      }

      this.acceptMarketingEmails = !additionalData.doNotBulkEmail;
      this.clientData = {
        leadType: additionalData.leadType,
        firstName: additionalData.firstName,
        middleName: additionalData.middleName,
        lastName: additionalData.lastName,
        email: additionalData.email,
        phoneNumber: additionalData.phoneNumber
      };
    }
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
